/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticationContext, useAuthQuery } from '@opengeoweb/api';
import {
  getAirmetConfig,
  getAirmetList,
  getAirmetTAC,
  getSigmetConfig,
  getSigmetList,
  getSigmetTAC,
  postAirmet,
  postSigmet,
} from './api';
import type {
  AirmetConfig,
  AirmetFromBackend,
  AirmetFromFrontend,
  AviationProduct,
  ProductType,
  SigmetConfig,
  SigmetFromBackend,
  SigmetFromFrontend,
} from '../types';

export const useAirmetList = (
  disabled = false,
): UseQueryResult<AirmetFromBackend[]> =>
  useAuthQuery(['airmet-list'], getAirmetList, disabled, false);

export const useSigmetList = (
  disabled = false,
): UseQueryResult<SigmetFromBackend[]> =>
  useAuthQuery(['sigmet-list'], getSigmetList, disabled, false);

export const useAviationWarnings = (): (
  | SigmetFromBackend
  | AirmetFromBackend
)[] => {
  const { data: airmets } = useAirmetList();
  const { data: sigmets } = useSigmetList();
  return (airmets || ([] as (SigmetFromBackend | AirmetFromBackend)[])).concat(
    sigmets || [],
  );
};
export const useProductConfig = (
  productType: ProductType,
): UseQueryResult<SigmetConfig | AirmetConfig> => {
  const airmetQuery = useAuthQuery(
    ['airmet-config'],
    getAirmetConfig,
    productType !== 'airmet',
  );

  const sigmetQuery = useAuthQuery(
    ['sigmet-config'],
    getSigmetConfig,
    productType !== 'sigmet',
  );

  return productType === 'airmet' ? airmetQuery : sigmetQuery;
};

type FetchTACData = Promise<{ data: string }>;
export const useTACApi = (): {
  fetchSigmetTAC: (product: AviationProduct) => FetchTACData;
  fetchAirmetTAC: (product: AviationProduct) => FetchTACData;
} => {
  const { auth } = useAuthenticationContext();
  const queryClient = useQueryClient();

  const fetchSigmetTAC = async (product: AviationProduct): FetchTACData =>
    queryClient.fetchQuery({
      queryKey: ['sigmet-tac', product],
      queryFn: async (): FetchTACData => getSigmetTAC(auth!, product),
      staleTime: 1000 * 15,
    });
  const fetchAirmetTAC = async (product: AviationProduct): FetchTACData =>
    queryClient.fetchQuery({
      queryKey: ['airmet-tac', product],
      queryFn: async (): FetchTACData => getAirmetTAC(auth!, product),
      staleTime: 1000 * 15,
    });

  return { fetchSigmetTAC, fetchAirmetTAC };
};

export const usePostSigmet = (): UseMutationResult<
  SigmetFromBackend,
  Error,
  SigmetFromFrontend
> => {
  const { auth } = useAuthenticationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: SigmetFromFrontend) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return postSigmet(auth, formData);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['sigmet'] });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sigmet-list'] });
    },
  });
};

export const usePostAirmet = (): UseMutationResult<
  AirmetFromBackend,
  Error,
  AirmetFromFrontend
> => {
  const { auth } = useAuthenticationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: AirmetFromFrontend) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return postAirmet(auth, formData);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['airmet'] });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['airmet-list'] });
    },
  });
};
